import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "@/components/AdminLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ADMIN_EMPLOYEES } from "@/constants/testIds";
import { toast } from "sonner";

export default function EmployeeNew() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", position: "", contract_type: "Indeterminado", hire_date: "", work_center: "", curp: "", rfc: "", nss: "", fecha_nacimiento: "", nacionalidad: "Mexicana", domicilio: "" });
  const [loading, setLoading] = useState(false);
  const [devPassword, setDevPassword] = useState(null);
  const [workCenters, setWorkCenters] = useState([]);

  useEffect(() => { api.get("/work-centers").then(({ data }) => setWorkCenters(data)); }, []);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const { data: preview } = await api.get("/billing/tier-preview");
      if (preview.would_change_band) {
        const nuevaBanda = preview.tier_after_one_more;
        const precio = nuevaBanda.contact_only ? "un plan a medida (contáctanos)" : `$${nuevaBanda.monthly_price.toLocaleString("es-MX")} MXN/mes`;
        const continuar = window.confirm(
          `Con este empleado pasas a la banda "${nuevaBanda.label}". Tu cobro cambiará a ${precio} desde el siguiente ciclo. ¿Quieres continuar?`
        );
        if (!continuar) return;
      }
    } catch (err) {
      // si no se pudo consultar la vista previa, no bloqueamos el alta por eso
    }
    setLoading(true);
    try {
      const { data } = await api.post("/employees", form);
      toast.success(data.message);
      if (data.dev_temp_password) {
        setDevPassword({ password: data.dev_temp_password, emailFailed: false });
      } else if (data.email_send_failed) {
        toast.error("No pudimos enviarle el correo de bienvenida. Entrégale la contraseña temporal manualmente.");
        setDevPassword({ password: data.temp_password, emailFailed: true });
      } else {
        navigate("/admin/empleados");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-lg">
        <h1 className="font-heading text-3xl font-bold text-charcoal tracking-tight mb-6">Nuevo empleado</h1>

        {devPassword ? (
          <div data-testid={devPassword.emailFailed ? ADMIN_EMPLOYEES.emailFailedBanner : ADMIN_EMPLOYEES.devTempPasswordBanner} className="bg-accent border border-terracotta/30 rounded-xl p-5">
            <p className="text-sm text-charcoal/80 mb-2">
              {devPassword.emailFailed
                ? "Cuenta creada. No pudimos enviar el correo de bienvenida (revisa Envíos fallidos). Entrégale esta contraseña temporal en persona:"
                : "Cuenta creada. Correo de bienvenida no enviado (Resend sin configurar). Contraseña temporal:"}
            </p>
            <p className="font-mono-legal font-semibold text-lg text-petroleum">{devPassword.password}</p>
            <button onClick={() => navigate("/admin/empleados")} className="mt-4 text-sm font-semibold text-petroleum underline">Volver a empleados</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-card border border-petroleum/10 rounded-2xl p-8 space-y-4 shadow-[0_8px_30px_rgba(14,79,79,0.06)]">
            <div>
              <Label>Nombre completo</Label>
              <Input data-testid={ADMIN_EMPLOYEES.fullNameInput} required value={form.full_name} onChange={update("full_name")} />
            </div>
            <div>
              <Label>Correo</Label>
              <Input data-testid={ADMIN_EMPLOYEES.emailInput} type="email" required value={form.email} onChange={update("email")} />
            </div>
            <div>
              <Label>Teléfono / WhatsApp</Label>
              <Input data-testid={ADMIN_EMPLOYEES.phoneInput} type="tel" required value={form.phone} onChange={update("phone")} placeholder="Ej. 55 1234 5678" />
            </div>
            <div>
              <Label>Puesto</Label>
              <Input data-testid={ADMIN_EMPLOYEES.positionInput} required value={form.position} onChange={update("position")} />
            </div>
            <div>
              <Label>Tipo de contrato</Label>
              <select data-testid={ADMIN_EMPLOYEES.contractTypeSelect} value={form.contract_type} onChange={update("contract_type")} className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                <option>Indeterminado</option>
                <option>Determinado</option>
                <option>Por obra</option>
              </select>
            </div>
            <div>
              <Label>Fecha de contratación</Label>
              <Input data-testid={ADMIN_EMPLOYEES.hireDateInput} type="date" required value={form.hire_date} onChange={update("hire_date")} />
            </div>
            <div>
              <Label>Centro de trabajo</Label>
              <select data-testid={ADMIN_EMPLOYEES.workCenterInput} value={form.work_center} onChange={update("work_center")} className="w-full mt-1 border border-petroleum/20 rounded-lg px-3 py-2 bg-white text-sm">
                <option value="">Sin centro asignado</option>
                {workCenters.map((wc) => <option key={wc.id} value={wc.name}>{wc.name}</option>)}
              </select>
              {workCenters.length === 0 && <p className="text-xs text-charcoal/40 mt-1">Crea centros de trabajo en el menú "Centros de Trabajo" para poder asignarlos aquí.</p>}
            </div>

            <p className="text-xs font-semibold text-charcoal/50 uppercase tracking-wide pt-2">Datos personales (opcional, se puede completar después)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CURP</Label>
                <Input data-testid={ADMIN_EMPLOYEES.curpInput} value={form.curp} onChange={update("curp")} />
              </div>
              <div>
                <Label>RFC</Label>
                <Input data-testid={ADMIN_EMPLOYEES.rfcInput} value={form.rfc} onChange={update("rfc")} />
              </div>
              <div>
                <Label>NSS</Label>
                <Input data-testid={ADMIN_EMPLOYEES.nssInput} value={form.nss} onChange={update("nss")} />
              </div>
              <div>
                <Label>Fecha de nacimiento</Label>
                <Input data-testid={ADMIN_EMPLOYEES.fechaNacimientoInput} type="date" value={form.fecha_nacimiento} onChange={update("fecha_nacimiento")} />
              </div>
              <div>
                <Label>Nacionalidad</Label>
                <Input data-testid={ADMIN_EMPLOYEES.nacionalidadInput} value={form.nacionalidad} onChange={update("nacionalidad")} />
              </div>
              <div>
                <Label>Domicilio</Label>
                <Input data-testid={ADMIN_EMPLOYEES.domicilioInput} value={form.domicilio} onChange={update("domicilio")} />
              </div>
            </div>
            <Button data-testid={ADMIN_EMPLOYEES.submitButton} type="submit" disabled={loading} className="w-full bg-petroleum hover:bg-petroleum/90 rounded-full h-11">
              {loading ? "Creando..." : "Crear empleado y cuenta"}
            </Button>
          </form>
        )}
      </div>
    </AdminLayout>
  );
}
